import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { formatMoney } from '@dorixona/utils'
import type { Product } from '../features/catalog/api'
import { FavoriteButton } from '../features/favorites/components/FavoriteButton'
import { env } from '../config/env'
import { Photo } from './Photo'
import { QtyStepper } from './QtyStepper'
import { categoryTint, priceLabel, productSubtitle } from './product'

export interface ProductCardProps {
  product: Product
  onOpen: (id: number) => void
  /** Сколько уже в корзине — тогда вместо «В корзину» показываем счётчик. */
  quantity?: number
  onAdd?: (product: Product) => void
  onChangeQuantity?: (product: Product, delta: number) => void
  /** На главной кнопок нет — только карточка. */
  compact?: boolean
}

export function ProductCard({
  product,
  onOpen,
  quantity = 0,
  onAdd,
  onChangeQuantity,
  compact = false,
}: ProductCardProps) {
  const { t } = useTranslation('catalog')
  const photo = product.photos[0]

  return (
    <Press
      squish="card"
      as="div"
      role="button"
      tabIndex={0}
      className="pcard"
      aria-label={product.name}
      onPress={() => {
        onOpen(product.id)
      }}
    >
      <div className="pimg" style={{ background: categoryTint(product.categoryId) }}>
        {product.rx ? (
          <span className="pill rx">{t('rx')}</span>
        ) : product.cb > 0 ? (
          <span className="pill cb num">{product.cb}%</span>
        ) : null}
        <Photo src={photo} alt="" iconSize={52} />
        {/* «Сердце» — во всех плитках сразу: каталог, полка, похожие, избранное. */}
        {env.features.favorites ? <FavoriteButton productId={product.id} /> : null}
      </div>
      <div className="pbody">
        <div className="pname">{product.name}</div>
        <div className="psub">{productSubtitle(product)}</div>
        <div className="pprice num">{priceLabel(product, formatMoney, t('priceUnknown'))}</div>

        {compact ? null : (
          <ProductAction
            product={product}
            quantity={quantity}
            onOpen={onOpen}
            onAdd={onAdd}
            onChangeQuantity={onChangeQuantity}
          />
        )}
      </div>
    </Press>
  )
}

interface ActionProps {
  product: Product
  quantity: number
  onOpen: (id: number) => void
  onAdd?: (product: Product) => void
  onChangeQuantity?: (product: Product, delta: number) => void
}

/**
 * Рецептурный товар в корзину не кладут — только самовывоз из аптеки (R-11).
 * Товара, которого нет в аптеках, тоже нельзя: заказ бы не собрался.
 */
function ProductAction({ product, quantity, onOpen, onAdd, onChangeQuantity }: ActionProps) {
  const { t } = useTranslation('catalog')
  // Кнопка внутри карточки: нажатие на неё не должно открывать товар.
  const stop = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  if (product.rx) {
    return (
      <Press
        squish="chip"
        className="padd rxonly"
        onClick={stop}
        onPress={() => {
          onOpen(product.id)
        }}
      >
        {t('pharmacyOnly')}
      </Press>
    )
  }

  // Ни одна аптека не даст и штуки (`max_qty = 0`) — класть в корзину нечего.
  if (!product.inStock || product.maxQty <= 0) {
    return (
      <Press
        squish="chip"
        className="padd rxonly"
        onClick={stop}
        onPress={() => {
          onOpen(product.id)
        }}
      >
        {t('outOfStock')}
      </Press>
    )
  }

  if (quantity > 0) {
    return (
      <QtyStepper
        className="padd-qty"
        productId={product.id}
        quantity={quantity}
        max={product.maxQty}
        onChange={(delta) => onChangeQuantity?.(product, delta)}
      />
    )
  }

  return (
    <Press
      squish="chip"
      haptic="key"
      className="padd"
      onClick={stop}
      onPress={() => onAdd?.(product)}
    >
      {t('addToCart')}
    </Press>
  )
}
